import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, ScanSearch, GitMerge } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Stat = ({ label, value, testId, tone = "text-foreground" }) => (
  <div className="rounded bg-secondary/60 p-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`font-heading text-xl ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

export default function DuplicateMergeCard() {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal memproses"); }
    finally { setBusy(false); }
  };
  const scan = () => run(async () => { setResult(null); const { data } = await api.get("/patients/duplicates"); setPreview(data); });
  const merge = () => run(async () => {
    const { data } = await api.post("/patients/duplicates/merge");
    setResult(data); setPreview(null);
    toast.success(`${data.duplicate_groups} grup duplikat ditemukan, ${data.merged_records} record dibersihkan`);
  });

  return (
    <Card data-testid="duplicate-merge-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Users className="h-4 w-4" /> Scan & Merge Duplikat Pasien</CardTitle>
        <CardDescription>
          Cari pasien dengan No HP sama (setelah dinormalisasi ke format 08xx) di klinik ini, lalu gabungkan riwayat transaksi,
          deposit, dan booking ke record terlengkap. Semua No HP juga dinormalisasi. Pasien tanpa No HP diabaikan.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button variant="outline" data-testid="dup-scan-btn" onClick={scan} disabled={busy}>
          <ScanSearch className="mr-1.5 h-4 w-4" /> {busy && !preview ? "Memindai..." : "Scan Duplikat"}
        </Button>

        {preview && (
          <div className="space-y-3 rounded-md border border-border p-4" data-testid="dup-preview">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <Stat label="Grup duplikat" value={preview.duplicate_groups} testId="dup-groups" tone="text-amber-700" />
              <Stat label="Record akan dihapus" value={preview.duplicate_records} testId="dup-records" tone="text-rose-700" />
              <Stat label="No HP dinormalisasi" value={preview.phones_to_normalize} testId="dup-normalize" />
            </div>
            {preview.groups.length > 0 && (
              <ul className="max-h-56 space-y-1 overflow-y-auto text-xs" data-testid="dup-group-list">
                {preview.groups.map((g) => (
                  <li key={g.phone} className="rounded bg-secondary/40 px-2 py-1">
                    <b>{g.phone}</b> — {g.patients.map((p) => `${p.name}${p.medical_record_no ? ` (${p.medical_record_no})` : ""}`).join(", ")}
                  </li>
                ))}
              </ul>
            )}
            <Button data-testid="dup-merge-btn" onClick={merge} disabled={busy || (!preview.duplicate_groups && !preview.phones_to_normalize)}>
              <GitMerge className="mr-1.5 h-4 w-4" /> {busy ? "Memproses..." : "Konfirmasi Merge"}
            </Button>
          </div>
        )}

        {result && (
          <div className="grid grid-cols-3 gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 p-4 text-sm" data-testid="dup-result">
            <Stat label="Duplikat ditemukan (grup)" value={result.duplicate_groups} testId="dup-result-groups" />
            <Stat label="Record dibersihkan" value={result.merged_records} testId="dup-result-merged" tone="text-emerald-700" />
            <Stat label="No HP dinormalisasi" value={result.phones_normalized} testId="dup-result-normalized" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
